import { useEffect, useState, useMemo, type ChangeEvent } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { ArrowLeft, ArrowRight, Download, FolderPlus } from 'react-bootstrap-icons';
import { schemaIssues } from '@/components/config/configFile';
import { JsonDiffView } from '@/components/config/JsonDiffView';
import { StepResult } from '@/components/config/StepList';
import { GroupedClientSelector } from '@/components/tools/GroupedClientSelector';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientService } from '@/services/clientService';
import { saveDeploymentGroup } from '@/services/deploymentService';
import { clientConfigSchema, type Client } from '@/types';
import { parseClientNamesFromCSV, exportClientNamesToCSV } from '@/utils/csvUtils';
import { getBulkUpdatableFields } from '@/config/clientConfigFields';
import './BulkUpdateClientConfig.css';

// FEAT-239 — derived from the shared field registry: every form-editable
// field, the bulk-only JSON/string fields, and remove-only cleanup entries
// for deprecated keys. Add new fields in src/config/clientConfigFields.ts.
const BULK_UPDATABLE_FIELDS = getBulkUpdatableFields();
const SETTING_FIELDS = BULK_UPDATABLE_FIELDS.filter((f) => !f.removeOnly);
const CLEANUP_FIELDS = BULK_UPDATABLE_FIELDS.filter((f) => f.removeOnly);

interface PreviewRow {
  clientName: string;
  currentValue: unknown;
  newValue: unknown;
  willChange: boolean;
}

interface UpdateResult {
  clientName: string;
  success: boolean;
  error?: string;
  skipped?: boolean;
}

interface CsvImportMessage {
  tone: 'success' | 'warning' | 'danger';
  text: string;
}

const clientsWord = (n: number) => (n === 1 ? '1 client' : `${n} clients`);

export default function BulkUpdateClientConfig() {
  // Client data
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedClientNames, setSelectedClientNames] = useState<string[]>([]);

  // Field selection
  const [selectedField, setSelectedField] = useState<string>('');
  const [booleanDraft, setBooleanDraft] = useState(false);
  const [enumDraft, setEnumDraft] = useState('');
  const [textDraft, setTextDraft] = useState('');
  const [unsetField, setUnsetField] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [previewAccepted, setPreviewAccepted] = useState(false);
  const [previewSelectedClient, setPreviewSelectedClient] = useState<string>('');

  // Execution state
  const [executing, setExecuting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<UpdateResult[] | null>(null);
  // The field the last run changed, so the result can name it after the form moves on.
  const [resultsField, setResultsField] = useState('');

  // CSV import state
  const [csvImportMessage, setCsvImportMessage] = useState<CsvImportMessage | null>(null);

  // Save as deployment group modal state
  const [showSaveGroupModal, setShowSaveGroupModal] = useState(false);
  const [saveGroupName, setSaveGroupName] = useState('');
  const [saveGroupDescription, setSaveGroupDescription] = useState('');
  const [saveGroupError, setSaveGroupError] = useState('');
  const [saveGroupBusy, setSaveGroupBusy] = useState(false);
  const [groupSaved, setGroupSaved] = useState<string | null>(null);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  // Reset value when field changes
  useEffect(() => {
    const fieldConfig = BULK_UPDATABLE_FIELDS.find((f) => f.key === selectedField);
    if (fieldConfig) {
      // Deprecated cleanup entries only support removal — force Unset on.
      setUnsetField(Boolean(fieldConfig.removeOnly));
      setValueError(null);
      if (fieldConfig.type === 'boolean') {
        setBooleanDraft(false);
      } else if (fieldConfig.type === 'enum' && fieldConfig.options) {
        setEnumDraft(fieldConfig.options[0].value);
        setTextDraft('');
      } else {
        setTextDraft('');
        setEnumDraft('');
      }
    }
  }, [selectedField]);

  // If inputs change after preview, require a fresh preview acceptance
  useEffect(() => {
    setPreviewAccepted(false);
  }, [selectedClientNames, selectedField, booleanDraft, enumDraft, textDraft, unsetField]);

  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await clientService.getAllClients();
      setClients(list);
    } catch (e) {
      const detail = e instanceof Error ? ` ${e.message}` : '';
      setError(`Could not load the client list. Reload the page to try again.${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClientToggle = (clientName: string) => {
    setSelectedClientNames((prev) =>
      prev.includes(clientName) ? prev.filter((n) => n !== clientName) : [...prev, clientName]
    );
  };

  const handleSelectClients = (clientNames: string[]) => {
    setSelectedClientNames(clientNames);
  };

  const handleCsvUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvImportMessage(null);
    try {
      const text = await file.text();
      const parsedNames = parseClientNamesFromCSV(text);

      if (parsedNames.length === 0) {
        setCsvImportMessage({
          tone: 'danger',
          text: 'Found no client names in the file. It needs a "Client Name" column.',
        });
        return;
      }

      // Match against loaded clients
      const validClientNames = clients.map((c) => c.name);
      const matchedNames = parsedNames.filter((name) => validClientNames.includes(name));
      const unmatchedCount = parsedNames.length - matchedNames.length;

      // Add to selection (union with existing)
      const newSelection = [...new Set([...selectedClientNames, ...matchedNames])];
      setSelectedClientNames(newSelection);

      if (unmatchedCount > 0) {
        setCsvImportMessage({
          tone: 'warning',
          text: `Ticked ${clientsWord(matchedNames.length)} from the file. ${unmatchedCount} ${unmatchedCount === 1 ? 'name' : 'names'} in the file matched no client.`,
        });
      } else {
        setCsvImportMessage({ tone: 'success', text: `Ticked ${clientsWord(matchedNames.length)} from the file.` });
      }
    } catch {
      setCsvImportMessage({ tone: 'danger', text: 'Could not read the file. Check that it is a CSV.' });
    }

    // Reset file input
    e.target.value = '';
  };

  const selectedFieldConfig = useMemo(
    () => BULK_UPDATABLE_FIELDS.find((f) => f.key === selectedField),
    [selectedField]
  );

  const canPreview = selectedClientNames.length > 0 && selectedField !== '';

  const validateGroupsValue = (value: unknown): string | null => {
    if (value === undefined) return null;
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return 'Groups must be a JSON object.';
    const obj = value as Record<string, unknown>;
    const admin = obj.admin;
    const standard = obj.standard;
    const isStringArray = (v: unknown) => Array.isArray(v) && v.every((x) => typeof x === 'string');
    if (admin !== undefined && !isStringArray(admin)) return '"admin" must be a list of text values.';
    if (standard !== undefined && !isStringArray(standard)) return '"standard" must be a list of text values.';
    return null;
  };

  const parseAndValidateNewValue = (): { ok: true; value: unknown } | { ok: false; error: string } => {
    if (!selectedFieldConfig) return { ok: false, error: 'Choose a field first.' };

    if (selectedFieldConfig.removeOnly || (selectedFieldConfig.allowUnset && unsetField)) {
      return { ok: true, value: undefined };
    }

    let value: unknown;
    if (selectedFieldConfig.type === 'boolean') {
      value = booleanDraft;
    } else if (selectedFieldConfig.type === 'enum') {
      value = enumDraft;
    } else if (selectedFieldConfig.type === 'string') {
      // An empty string passes z.string() but breaks infra's `?? default`
      // fallbacks (e.g. customDomain '') — removal is what's actually meant.
      if (textDraft.trim() === '') {
        return {
          ok: false,
          error: selectedFieldConfig.allowUnset
            ? 'The value is empty. To take the field out of the config, tick "Remove this field from the config".'
            : 'Enter a value.',
        };
      }
      value = textDraft.trim();
    } else if (selectedFieldConfig.type === 'number') {
      if (textDraft.trim() === '') return { ok: false, error: 'Enter a number.' };
      const num = Number(textDraft);
      if (!Number.isFinite(num)) return { ok: false, error: 'That is not a number.' };
      value = num;
    } else if (selectedFieldConfig.type === 'json') {
      if (textDraft.trim() === '') return { ok: false, error: 'Enter the value as JSON.' };
      try {
        value = JSON.parse(textDraft);
      } catch (e) {
        return { ok: false, error: `That is not valid JSON.${e instanceof Error ? ` ${e.message}` : ''}` };
      }
    } else {
      return { ok: false, error: 'This field cannot be changed in bulk.' };
    }

    // groups gets a friendlier shape check first, then falls through to the
    // zod pick below (the early return here used to skip zod entirely).
    if (selectedFieldConfig.key === 'groups') {
      const groupsError = validateGroupsValue(value);
      if (groupsError) return { ok: false, error: groupsError };
    }

    try {
      clientConfigSchema
        .partial()
        .pick({ [selectedFieldConfig.key]: true } as any)
        .parse({ [selectedFieldConfig.key]: value } as any);
    } catch (e) {
      const issues = schemaIssues(e);
      return {
        ok: false,
        error: issues
          ? `The config does not accept that value. ${issues}.`
          : e instanceof Error
            ? e.message
            : 'The config does not accept that value.',
      };
    }

    return { ok: true, value };
  };

  const areValuesEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true;
    if (a === undefined || b === undefined) return false;
    if (a === null || b === null) return false;
    if (typeof a !== 'object' || typeof b !== 'object') return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  const handlePreview = () => {
    if (!canPreview || !selectedFieldConfig) return;

    const parsed = parseAndValidateNewValue();
    if (!parsed.ok) {
      setValueError(parsed.error);
      return;
    }
    setValueError(null);

    const nextValue = parsed.value;
    const preview: PreviewRow[] = selectedClientNames.map((clientName) => {
      const client = clients.find((c) => c.name === clientName);
      const currentValue = (client?.config as any)?.[selectedFieldConfig.key];
      return {
        clientName,
        currentValue,
        newValue: nextValue,
        willChange: !areValuesEqual(currentValue, nextValue),
      };
    });

    setPreviewData(preview);
    setPreviewAccepted(false);
    setPreviewSelectedClient(preview[0]?.clientName || '');
    setGroupSaved(null);
    setShowPreview(true);
  };

  const handleExecute = async () => {
    if (!selectedFieldConfig || previewData.length === 0 || !previewAccepted) return;

    setExecuting(true);
    setProgress(0);
    setResults(null);
    setResultsField(selectedFieldConfig.label);

    const updateResults: UpdateResult[] = [];

    for (let i = 0; i < previewData.length; i++) {
      const { clientName, willChange, newValue } = previewData[i];

      if (!willChange) {
        // Skip clients that don't need updating
        updateResults.push({ clientName, success: true, skipped: true });
      } else {
        try {
          await clientService.updateClientConfig(clientName, { [selectedFieldConfig.key]: newValue } as any);
          updateResults.push({ clientName, success: true });
        } catch (err) {
          updateResults.push({
            clientName,
            success: false,
            error: err instanceof Error ? err.message : 'Unknown error',
          });
        }
      }

      setProgress(Math.round(((i + 1) / previewData.length) * 100));
    }

    setResults(updateResults);
    setExecuting(false);
    setShowPreview(false);

    // Refresh client list to show updated values
    await loadClients();
  };

  // Export clients that will change to CSV
  const handleExportChangedClients = () => {
    const changedClients = previewData.filter((row) => row.willChange).map((r) => r.clientName);
    if (changedClients.length === 0) return;
    exportClientNamesToCSV(changedClients, `changed-clients-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // Open save as deployment group modal
  const handleOpenSaveGroupModal = () => {
    setSaveGroupName('');
    setSaveGroupDescription('');
    setSaveGroupError('');
    setGroupSaved(null);
    setShowSaveGroupModal(true);
  };

  // Save changed clients as a deployment group
  const handleSaveAsDeploymentGroup = async () => {
    const changedClients = previewData.filter((row) => row.willChange).map((r) => r.clientName);
    if (changedClients.length === 0) return;

    if (!saveGroupName.trim()) {
      setSaveGroupError('Give the group a name.');
      return;
    }

    setSaveGroupBusy(true);
    setSaveGroupError('');

    try {
      await saveDeploymentGroup({
        groupName: saveGroupName.trim(),
        clients: changedClients,
        description: saveGroupDescription.trim() || undefined,
      });
      setShowSaveGroupModal(false);
      setGroupSaved(`Saved the deployment group "${saveGroupName.trim()}" with ${clientsWord(changedClients.length)}.`);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setSaveGroupError(`The group was not saved.${detail}`);
    } finally {
      setSaveGroupBusy(false);
    }
  };

  // Skipped clients are NOT successes — report them separately so
  // "N updated" always means N actual writes.
  const successCount = results?.filter((r) => r.success && !r.skipped).length ?? 0;
  const skippedCount = results?.filter((r) => r.skipped).length ?? 0;
  const failureCount = results?.filter((r) => !r.success).length ?? 0;
  const changedCount = previewData.filter((p) => p.willChange).length;
  const previewNewValue = previewData.length > 0 ? previewData[0].newValue : undefined;
  const previewSelectedClientConfig = clients.find((c) => c.name === previewSelectedClient)?.config;
  const previewClientIndex = previewData.findIndex((p) => p.clientName === previewSelectedClient);

  const goToAdjacentPreviewClient = (direction: 'prev' | 'next') => {
    if (previewData.length === 0) return;
    const currentIndex = previewClientIndex >= 0 ? previewClientIndex : 0;
    const nextIndex =
      direction === 'prev'
        ? (currentIndex - 1 + previewData.length) % previewData.length
        : (currentIndex + 1) % previewData.length;
    setPreviewSelectedClient(previewData[nextIndex].clientName);
  };

  const buildUpdatedConfigForPreview = (config: unknown, key: string, value: unknown): unknown => {
    if (!key) return config;
    if (!config || typeof config !== 'object') return config;
    let next: any;
    try {
      next = JSON.parse(JSON.stringify(config));
    } catch {
      next = { ...(config as any) };
    }
    if (value === undefined) {
      delete next[key];
      return next;
    }
    next[key] = value;
    return next;
  };

  const formatValue = (value: unknown): string => {
    if (value === undefined) return '(not set)';
    if (value === null) return '(null)';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'object') {
      try {
        const s = JSON.stringify(value);
        return s.length > 160 ? `${s.slice(0, 157)}...` : s;
      } catch {
        return '(object)';
      }
    }
    return String(value);
  };

  /** A new value of `undefined` means the field is taken out of the config. */
  const formatNewValue = (value: unknown): string => (value === undefined ? '(removed)' : formatValue(value));

  return (
    <ToolPage title="Bulk update client config" subtitle="Change one config field on many clients at once.">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* What the last run did */}
      {results && (
        <Alert variant={failureCount === 0 ? 'success' : 'warning'} dismissible onClose={() => setResults(null)}>
          <strong>
            {failureCount === 0
              ? `Changed ${resultsField} on ${clientsWord(successCount)}.`
              : `${clientsWord(failureCount)} could not be changed.`}
          </strong>
          <div>
            {successCount} changed, {skippedCount} skipped because they already held the value, {failureCount} failed. A
            changed client picks the new value up at its next deploy.
          </div>
          {failureCount > 0 && (
            <ul className="mt-2 mb-0">
              {results
                .filter((r) => !r.success)
                .map((r) => (
                  <li key={r.clientName}>
                    <span className="mono">{r.clientName}</span>: {r.error}
                  </li>
                ))}
            </ul>
          )}
        </Alert>
      )}

      <Row>
        {/* Left column: which clients */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header>1. Choose the clients</Card.Header>
            <Card.Body>
              <Form.Group controlId="bulk-csv" className="mb-3">
                <Form.Label>From a CSV file</Form.Label>
                <Form.Control type="file" accept=".csv" onChange={handleCsvUpload} disabled={loading} size="sm" />
                <Form.Text>
                  A CSV exported from Config search ticks its clients for you. The file needs a "Client Name" column.
                </Form.Text>
                {csvImportMessage && <StepResult tone={csvImportMessage.tone}>{csvImportMessage.text}</StepResult>}
              </Form.Group>

              <Form.Label>Or tick them here</Form.Label>
              {loading ? (
                <div className="bulk-update__loading">
                  <Spinner size="sm" className="me-2" />
                  Loading clients
                </div>
              ) : (
                <GroupedClientSelector
                  clients={clients}
                  selectedClientNames={selectedClientNames}
                  onClientToggle={handleClientToggle}
                  onSelectClients={handleSelectClients}
                  disabled={loading}
                />
              )}

              <div className="bulk-update__count">{clientsWord(selectedClientNames.length)} chosen</div>
            </Card.Body>
          </Card>
        </Col>

        {/* Right column: which field, and its new value */}
        <Col lg={6} className="mb-3">
          <Card className="h-100">
            <Card.Header>2. Choose the change</Card.Header>
            <Card.Body>
              <div className="tool-form">
                <section className="tool-form__section">
                  <Form.Group className="mb-3" controlId="bulk-field">
                    <Form.Label>Field to change</Form.Label>
                    <Form.Select
                      value={selectedField}
                      onChange={(e) => setSelectedField(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Choose a field</option>
                      <optgroup label="Settings">
                        {SETTING_FIELDS.map((field) => (
                          <option key={field.key} value={field.key}>
                            {field.label}
                          </option>
                        ))}
                      </optgroup>
                      {CLEANUP_FIELDS.length > 0 && (
                        <optgroup label="Deprecated (remove only)">
                          {CLEANUP_FIELDS.map((field) => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </Form.Select>
                    {selectedFieldConfig && <Form.Text>{selectedFieldConfig.description}</Form.Text>}
                  </Form.Group>

                  {selectedFieldConfig && (
                    <Form.Group>
                      <Form.Label htmlFor="bulk-value">New value</Form.Label>
                      {selectedFieldConfig.removeOnly && (
                        <Alert variant="warning" className="mb-2">
                          <strong>This field is deprecated, so it can only be removed.</strong>{' '}
                          {selectedFieldConfig.removeOnlyReason} It is taken out of every chosen config that holds it,
                          and clients without it are skipped. Check that the chosen clients run, or will next deploy, an
                          image that no longer reads it.
                        </Alert>
                      )}
                      {selectedFieldConfig.allowUnset && (
                        <Form.Check
                          className="mb-2"
                          type="checkbox"
                          id="unset-field"
                          checked={unsetField}
                          disabled={selectedFieldConfig.removeOnly}
                          onChange={(e) => {
                            setUnsetField(e.target.checked);
                            setValueError(null);
                          }}
                          label="Remove this field from the config"
                        />
                      )}
                      {selectedFieldConfig.removeOnly ? null : selectedFieldConfig.type === 'boolean' ? (
                        <Form.Check
                          type="switch"
                          id="bulk-value"
                          label={booleanDraft ? 'On (true)' : 'Off (false)'}
                          checked={booleanDraft === true}
                          onChange={(e) => {
                            setBooleanDraft(e.target.checked);
                            setValueError(null);
                          }}
                          disabled={unsetField}
                        />
                      ) : selectedFieldConfig.type === 'enum' && selectedFieldConfig.options ? (
                        <Form.Select
                          id="bulk-value"
                          value={enumDraft}
                          onChange={(e) => {
                            setEnumDraft(e.target.value);
                            setValueError(null);
                          }}
                          disabled={unsetField}
                        >
                          {selectedFieldConfig.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Form.Select>
                      ) : selectedFieldConfig.type === 'string' ? (
                        <Form.Control
                          id="bulk-value"
                          type="text"
                          value={textDraft}
                          onChange={(e) => {
                            setTextDraft(e.target.value);
                            setValueError(null);
                          }}
                          disabled={unsetField}
                          placeholder="The new value"
                        />
                      ) : selectedFieldConfig.type === 'number' ? (
                        <Form.Control
                          id="bulk-value"
                          type="number"
                          value={textDraft}
                          onChange={(e) => {
                            setTextDraft(e.target.value);
                            setValueError(null);
                          }}
                          disabled={unsetField}
                          placeholder="A number"
                        />
                      ) : selectedFieldConfig.type === 'json' ? (
                        <Form.Control
                          id="bulk-value"
                          as="textarea"
                          rows={6}
                          value={textDraft}
                          onChange={(e) => {
                            setTextDraft(e.target.value);
                            setValueError(null);
                          }}
                          disabled={unsetField}
                          placeholder='{"key": "value"}'
                          className="bulk-update__json"
                        />
                      ) : null}
                      {valueError && (
                        <Alert variant="danger" className="mt-2 mb-0">
                          {valueError}
                        </Alert>
                      )}
                    </Form.Group>
                  )}
                </section>

                <div className="tool-form__actions">
                  <Button variant="primary" onClick={handlePreview} disabled={!canPreview || loading}>
                    Preview changes
                  </Button>
                  <span className="tool-form__hint">
                    {canPreview
                      ? 'Nothing changes until you check the preview and apply it.'
                      : 'Choose at least one client and a field.'}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Preview, then apply */}
      <Modal
        show={showPreview}
        onHide={() => !executing && setShowPreview(false)}
        size="lg"
        centered
        backdrop={executing ? 'static' : true}
      >
        <Modal.Header closeButton={!executing}>
          <Modal.Title>{executing ? 'Applying the change' : 'Preview changes'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {executing ? (
            <div className="py-4">
              <ProgressBar now={progress} label={`${progress}%`} animated striped />
              <p className="bulk-update__progress">Changing clients one at a time. Keep this page open.</p>
            </div>
          ) : (
            <>
              {groupSaved && (
                <Alert variant="success" dismissible onClose={() => setGroupSaved(null)}>
                  {groupSaved}
                </Alert>
              )}

              <dl className="tool-facts mb-3">
                <dt>Field</dt>
                <dd>
                  {selectedFieldConfig?.label} <span className="mono">({selectedFieldConfig?.key})</span>
                </dd>
                <dt>New value</dt>
                <dd className="mono">{formatNewValue(previewNewValue)}</dd>
                <dt>Clients</dt>
                <dd>
                  {changedCount} of {previewData.length} change.{' '}
                  {changedCount < previewData.length && 'The others already hold this value and are skipped.'}
                </dd>
              </dl>

              <Form.Label htmlFor="bulk-preview-client">See the whole config of one client</Form.Label>
              <div className="bulk-update__pager">
                <Button
                  variant="outline-secondary"
                  onClick={() => goToAdjacentPreviewClient('prev')}
                  disabled={previewData.length <= 1}
                  aria-label="Previous client"
                >
                  <ArrowLeft />
                </Button>
                <Form.Select
                  id="bulk-preview-client"
                  value={previewSelectedClient}
                  onChange={(e) => setPreviewSelectedClient(e.target.value)}
                >
                  {previewData.map((row) => (
                    <option key={row.clientName} value={row.clientName}>
                      {row.clientName}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="outline-secondary"
                  onClick={() => goToAdjacentPreviewClient('next')}
                  disabled={previewData.length <= 1}
                  aria-label="Next client"
                >
                  <ArrowRight />
                </Button>
              </div>
              <div className="bulk-update__diff">
                <JsonDiffView
                  before={previewSelectedClientConfig as Record<string, unknown> | undefined}
                  after={
                    buildUpdatedConfigForPreview(
                      previewSelectedClientConfig,
                      selectedFieldConfig?.key || '',
                      previewData.find((r) => r.clientName === previewSelectedClient)?.newValue
                    ) as Record<string, unknown> | undefined
                  }
                />
              </div>

              <h3 className="section-label">Every chosen client</h3>
              <div className="bulk-update__table">
                <Table className="mb-0">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Current value</th>
                      <th>New value</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row) => (
                      <tr key={row.clientName}>
                        <td>{row.clientName}</td>
                        <td className="mono">{formatValue(row.currentValue)}</td>
                        <td className="mono">{formatNewValue(row.newValue)}</td>
                        <td>
                          {row.willChange ? (
                            <Badge bg="warning">Will change</Badge>
                          ) : (
                            <Badge bg="secondary">No change</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Form.Check
                className="mt-3"
                type="checkbox"
                id="bulk-update-accept-preview"
                checked={previewAccepted}
                onChange={(e) => setPreviewAccepted(e.target.checked)}
                label={`I checked this preview. Change ${clientsWord(changedCount)}.`}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!executing && (
            <>
              <Button variant="outline-secondary" onClick={() => setShowPreview(false)}>
                Cancel
              </Button>
              <Button variant="outline-secondary" onClick={handleExportChangedClients} disabled={changedCount === 0}>
                <Download className="me-1" aria-hidden="true" />
                Export the changed clients
              </Button>
              <Button variant="outline-secondary" onClick={handleOpenSaveGroupModal} disabled={changedCount === 0}>
                <FolderPlus className="me-1" aria-hidden="true" />
                Save as a deployment group
              </Button>
              <Button variant="primary" onClick={handleExecute} disabled={changedCount === 0 || !previewAccepted}>
                Apply to {clientsWord(changedCount)}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Save the changed clients as a deployment group */}
      <Modal show={showSaveGroupModal} onHide={() => !saveGroupBusy && setShowSaveGroupModal(false)} centered>
        <Modal.Header closeButton={!saveGroupBusy}>
          <Modal.Title>Save as a deployment group</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {saveGroupError && <Alert variant="danger">{saveGroupError}</Alert>}
          <p>
            This makes a deployment group of the {clientsWord(changedCount)} this change touches, so you can deploy them
            together afterwards. No config changes.
          </p>
          <Form.Group className="mb-3" controlId="bulk-group-name">
            <Form.Label>Group name</Form.Label>
            <Form.Control
              type="text"
              value={saveGroupName}
              onChange={(e) => setSaveGroupName(e.target.value)}
              placeholder="Region change, October"
              disabled={saveGroupBusy}
            />
            <Form.Text>No other group can have this name.</Form.Text>
          </Form.Group>
          <Form.Group controlId="bulk-group-description">
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={saveGroupDescription}
              onChange={(e) => setSaveGroupDescription(e.target.value)}
              placeholder="What this group is for"
              disabled={saveGroupBusy}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSaveGroupModal(false)} disabled={saveGroupBusy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAsDeploymentGroup}
            disabled={saveGroupBusy || !saveGroupName.trim()}
          >
            {saveGroupBusy ? <Spinner size="sm" className="me-2" /> : null}
            Save group
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
